import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import apiClient from '@/services/apiClient';
import { isDemoMode, demoSubscriptionState } from '@/config/demo';

export type SubscriptionPlan = 'free' | 'paid';
export type AccessState = 'full' | 'trial' | 'expired';
export type SubscriptionStatus = 'active' | 'canceled' | 'expired';

export interface SubscriptionDetail {
  status: SubscriptionStatus;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
}

export interface SubscriptionMeResponse {
  plan: SubscriptionPlan;
  accessState: AccessState;
  trialEndsAt: string | null;
  subscription: SubscriptionDetail | null;
}

export interface SubscribeResponse {
  checkoutUrl: string;
  expiresAt: string;
  sessionId: string;
}

export type CheckoutStatus = 'completed' | 'checkout_ready' | 'failed' | 'canceled' | 'expired';

export interface CheckoutStatusResponse {
  status: CheckoutStatus;
}

export const subscriptionApi = {
  subscribe: async (): Promise<SubscribeResponse> => {
    const response = await apiClient.post<SubscribeResponse>('/api/v1/subscription/subscribe');
    return response.data;
  },
  getMe: async (): Promise<SubscriptionMeResponse> => {
    const response = await apiClient.get<SubscriptionMeResponse>('/api/v1/subscription/me');
    return response.data;
  },
  cancel: async (): Promise<SubscriptionDetail> => {
    const response = await apiClient.post<SubscriptionDetail>('/api/v1/subscription/cancel');
    return response.data;
  },
  resume: async (): Promise<SubscriptionDetail> => {
    const response = await apiClient.post<SubscriptionDetail>('/api/v1/subscription/resume');
    return response.data;
  },
  getCheckoutStatus: async (sessionId: string): Promise<CheckoutStatusResponse> => {
    const response = await apiClient.get<CheckoutStatusResponse>(
      `/api/v1/subscription/checkout/${sessionId}`
    );
    return response.data;
  },
};

export const queryKeys = {
  subscription: {
    all: ['subscription'] as const,
    me: () => [...queryKeys.subscription.all, 'me'] as const,
    checkout: (sessionId: string) =>
      [...queryKeys.subscription.all, 'checkout', sessionId] as const,
  },
};

// Demo 模式：依 VITE_DEMO_SUBSCRIPTION_STATE 回傳模擬的訂閱狀態，不打後端。
const buildDemoSubscriptionMe = (): SubscriptionMeResponse => {
  const periodEnd = new Date(Date.now() + 30 * 86_400_000).toISOString();
  const trialEnd = new Date(Date.now() + 7 * 86_400_000).toISOString();
  switch (demoSubscriptionState) {
    case 'scheduled_cancel':
      return {
        plan: 'paid',
        accessState: 'full',
        trialEndsAt: null,
        subscription: { status: 'active', currentPeriodEnd: periodEnd, cancelAtPeriodEnd: true },
      };
    case 'trial':
      return { plan: 'free', accessState: 'trial', trialEndsAt: trialEnd, subscription: null };
    case 'expired':
      return { plan: 'free', accessState: 'expired', trialEndsAt: null, subscription: null };
    case 'none':
      return { plan: 'free', accessState: 'full', trialEndsAt: null, subscription: null };
    case 'active':
    default:
      return {
        plan: 'paid',
        accessState: 'full',
        trialEndsAt: null,
        subscription: { status: 'active', currentPeriodEnd: periodEnd, cancelAtPeriodEnd: false },
      };
  }
};

interface UseSubscriptionMeOptions {
  enabled?: boolean;
  refetchInterval?: UseQueryOptions<SubscriptionMeResponse>['refetchInterval'];
}

export const useSubscriptionMe = (options: UseSubscriptionMeOptions = {}) => {
  const { enabled = true, refetchInterval } = options;
  return useQuery({
    queryKey: queryKeys.subscription.me(),
    queryFn: isDemoMode ? () => Promise.resolve(buildDemoSubscriptionMe()) : subscriptionApi.getMe,
    enabled,
    refetchInterval,
  });
};

export const useSubscribe = () =>
  useMutation({
    mutationFn: subscriptionApi.subscribe,
  });

export const useCancelSubscription = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: subscriptionApi.cancel,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.subscription.all });
    },
  });
};

export const useResumeSubscription = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: subscriptionApi.resume,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.subscription.all });
    },
  });
};

interface UseCheckoutStatusOptions {
  enabled?: boolean;
}

export const useCheckoutStatus = (
  sessionId: string | null,
  options: UseCheckoutStatusOptions = {}
) => {
  return useQuery({
    queryKey: queryKeys.subscription.checkout(sessionId ?? ''),
    queryFn: () => subscriptionApi.getCheckoutStatus(sessionId!),
    enabled: (options.enabled ?? true) && !!sessionId,
  });
};
